import React, { useState, useEffect } from "react";
import {
  HardDrive,
  Cpu,
  Database,
  ShieldCheck,
  ShieldAlert,
  RefreshCw,
  Terminal,
  Activity,
  CheckCircle,
} from "lucide-react";
import { api } from "../../../lib/api";
import { toast } from "sonner";

function formatLogTime(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return ts;
  }
}

export default function HealthTab() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadHealth = async () => {
    setLoading(true);
    try {
      const res = await api.get("/super-admin/system/health");
      setHealth(res.data || null);
    } catch (err) {
      toast.error("Failed to load system health telemetry");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHealth();
  }, []);

  const disk = health?.disk || {};
  const integrityList = health?.database_integrity || [];
  const allDbsHealthy = integrityList.length > 0 && integrityList.every((d) => d.ok);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-400" />
            <span>System Health & Database Integrity</span>
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Host disk telemetry, runtime environment, and live PRAGMA integrity checks on all SQLite databases.
          </p>
        </div>

        <button
          onClick={loadHealth}
          disabled={loading}
          className="px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-800 hover:bg-slate-800 text-slate-300 text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50 self-start md:self-auto"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-teal-400" : ""}`} />
          <span>Run Integrity Scan</span>
        </button>
      </div>

      {/* Grid: Disk Usage & System Specifications */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Disk Usage Card */}
        <div className="lg:col-span-1 bg-slate-900/70 border border-slate-800 rounded-2xl p-6 shadow-xl flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Data Volume Disk Capacity
              </span>
              <HardDrive className="w-4 h-4 text-emerald-400" />
            </div>

            <div className="text-3xl font-black text-white tracking-tight">
              {disk.used_percent != null ? `${disk.used_percent}%` : "—"}
            </div>
            <div className="text-xs text-slate-400 mt-1">
              Used {disk.used_gb || 0} GB of {disk.total_gb || 0} GB
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-slate-950 rounded-full h-3 mt-4 overflow-hidden border border-slate-800">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  (disk.used_percent || 0) > 85
                    ? "bg-rose-500"
                    : (disk.used_percent || 0) > 70
                    ? "bg-amber-500"
                    : "bg-gradient-to-r from-teal-500 to-emerald-400"
                }`}
                style={{ width: `${Math.min(disk.used_percent || 0, 100)}%` }}
              />
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-800 text-[11px] text-slate-500 flex justify-between">
            <span>Free Disk Space:</span>
            <span className="text-slate-300 font-semibold">{disk.free_gb || 0} GB</span>
          </div>
        </div>

        {/* Runtime Environment Specs */}
        <div className="lg:col-span-2 bg-slate-900/70 border border-slate-800 rounded-2xl p-6 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">
              Server Runtime Telemetry
            </span>
            <Cpu className="w-4 h-4 text-teal-400" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800/80">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Host OS</div>
              <div className="text-sm font-bold text-white mt-1">{health?.os || "Linux"}</div>
              <div className="text-[10px] text-slate-400 truncate">{health?.os_release || ""}</div>
            </div>

            <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800/80">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Python</div>
              <div className="text-sm font-bold text-white mt-1">{health?.python_version || "3.11"}</div>
              <div className="text-[10px] text-emerald-400 font-semibold">AsyncIO Ready</div>
            </div>

            <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800/80">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Active Sessions</div>
              <div className="text-sm font-bold text-white mt-1">{health?.active_sessions_total || 0}</div>
              <div className="text-[10px] text-slate-400">Live tokens</div>
            </div>

            <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800/80">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Overall State</div>
              <div className="text-sm font-bold text-white mt-1">
                {allDbsHealthy ? (
                  <span className="text-emerald-400 flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" /> Healthy
                  </span>
                ) : (
                  <span className="text-amber-400 flex items-center gap-1">
                    <ShieldAlert className="w-3.5 h-3.5" /> Check
                  </span>
                )}
              </div>
              <div className="text-[10px] text-slate-400">All databases</div>
            </div>
          </div>

          <div className="mt-4 p-3 rounded-xl bg-slate-950/40 border border-slate-800/60 flex items-center gap-2 text-xs font-mono text-slate-400">
            <Terminal className="w-3.5 h-3.5 text-teal-400 shrink-0" />
            <span className="text-slate-500">Storage Root:</span>
            <span className="text-slate-300 truncate">{health?.data_directory || "/data"}</span>
          </div>
        </div>
      </div>

      {/* Database Integrity Scan Table */}
      <div className="bg-slate-900/70 border border-slate-800 rounded-2xl shadow-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-bold text-white tracking-tight">SQLite Database Files & Integrity Checks</h3>
          </div>
          <span className="text-xs text-slate-500">({integrityList.length} total database files scanned)</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/60 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                <th className="py-2.5 px-4">Database Description</th>
                <th className="py-2.5 px-4">Relative Path</th>
                <th className="py-2.5 px-4">File Size</th>
                <th className="py-2.5 px-4">Last Modified</th>
                <th className="py-2.5 px-4">Integrity Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 text-slate-300">
              {integrityList.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-slate-500 text-xs">
                    {loading ? "Scanning SQLite headers..." : "No databases scanned."}
                  </td>
                </tr>
              ) : (
                integrityList.map((db, idx) => {
                  return (
                    <tr key={idx} className="hover:bg-slate-800/40 transition-colors">
                      <td className="py-3 px-4 font-bold text-white">
                        {db.database || db.name || "—"}
                      </td>
                      <td className="py-3 px-4 font-mono text-slate-400 text-[11px]">
                        {db.file || db.relative_path || db.path || "—"}
                      </td>
                      <td className="py-3 px-4 font-semibold text-slate-300">
                        {db.size_formatted || "—"}
                      </td>
                      <td className="py-3 px-4 text-slate-400 text-[11px] whitespace-nowrap">
                        {formatLogTime(db.last_modified)}
                      </td>
                      <td className="py-3 px-4">
                        {db.ok ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                            <ShieldCheck className="w-3 h-3" /> Integrity OK
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-rose-500/15 text-rose-400 border border-rose-500/30">
                            <ShieldAlert className="w-3 h-3" /> {db.integrity_check || db.status || "Corrupted"}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
